/**
 * Whether a *registered* player (registered for an event, but not an invited
 * event admin/referee) may ref/score matches in that event.
 *
 * Cascade, mirroring `resolveKFactorForEvent`:
 *   1. event.settings.allowRegisteredPlayersToRef  (explicit per-event override)
 *   2. federation.allow_registered_players_to_ref  (org default)
 *   3. true                                          (hard default — preserve legacy behaviour)
 */
export function resolveAllowRegisteredPlayersToRef(
  eventSettings: { allowRegisteredPlayersToRef?: boolean } | null | undefined,
  federationAllowsRegisteredPlayersToRef: boolean | null | undefined,
): boolean {
  if (typeof eventSettings?.allowRegisteredPlayersToRef === 'boolean') {
    return eventSettings.allowRegisteredPlayersToRef;
  }
  if (typeof federationAllowsRegisteredPlayersToRef === 'boolean') {
    return federationAllowsRegisteredPlayersToRef;
  }
  return true;
}

/**
 * Keep only the events a registered player is allowed to ref/score, applying the
 * per-event override (`event.settings.allowRegisteredPlayersToRef`) on top of the
 * federation default supplied in `federationAllowsById`. Order is preserved.
 */
export function filterRegisteredEventsRefEligible<
  T extends { settings: unknown; federation_id: string | null },
>(events: T[], federationAllowsById: Map<string, boolean | null | undefined>): T[] {
  return events.filter((event) => {
    const settings = (event.settings ?? null) as {
      allowRegisteredPlayersToRef?: boolean;
    } | null;
    const federationAllows = event.federation_id
      ? federationAllowsById.get(event.federation_id)
      : undefined;
    return resolveAllowRegisteredPlayersToRef(settings, federationAllows ?? null);
  });
}
